// src/Context/CartContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { syncCartAPI, getCartAPI } from '../pages/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper to clean price strings (remove currency symbols, handle commas)
const cleanPrice = (price) => {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') {
    const cleaned = price.replace(',', '.').replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper: recalc count & subtotal (using basePrice)
  const calculateCartSummary = useCallback((items) => {
    const count = items.reduce((total, item) => total + (item.qty || 1), 0);
    const total = items.reduce((sum, item) => {
      const price = cleanPrice(item.basePrice) || 0;
      return sum + price * (item.qty || 1);
    }, 0);
    return { count, total };
  }, []);

  // Load cart from localStorage on mount (anonymous users)
  useEffect(() => {
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        const itemsWithQty = parsed.map(item => {
          const parts = [item.id || item.productId || Date.now()];
          if (item.selectedSizeId) parts.push(item.selectedSizeId);
          if (item.selectedFragranceId) parts.push(item.selectedFragranceId);
          const uniqueId = parts.join('-');

          const basePrice = cleanPrice(item.basePrice) || cleanPrice(item.price) || cleanPrice(item.priceValue) || 0;

          return {
            ...item,
            qty: item.qty || 1,
            basePrice: basePrice,
            price: basePrice,
            priceValue: basePrice,
            uniqueId: item.uniqueId || uniqueId,
          };
        });
        setCartItems(itemsWithQty);
        const { count, total } = calculateCartSummary(itemsWithQty);
        setCartCount(count);
        setSubtotal(total);
      } catch (error) {
        console.error('CartProvider: Error loading cart from localStorage', error);
        setCartItems([]);
      }
    }
    setIsInitialized(true);
  }, [calculateCartSummary]);

  // Save to localStorage whenever cartItems change (after init)
  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
    const { count, total } = calculateCartSummary(cartItems);
    setCartCount(count);
    setSubtotal(total);
  }, [cartItems, isInitialized, calculateCartSummary]);

  // ADD TO CART – ensure basePrice is set
  const addToCart = useCallback((product, quantity = 1) => {
    setCartItems(prevItems => {
      const parts = [product.id || product.productId || Date.now()];
      if (product.selectedSizeId) parts.push(product.selectedSizeId);
      if (product.selectedFragranceId) parts.push(product.selectedFragranceId);
      const uniqueId = parts.join('-');

      const existing = prevItems.find(item => item.uniqueId === uniqueId);
      if (existing) {
        return prevItems.map(item =>
          item.uniqueId === uniqueId
            ? { ...item, qty: (item.qty || 1) + quantity, updatedAt: Date.now() }
            : item
        );
      }

      const basePrice = cleanPrice(product.price) || cleanPrice(product.priceValue) || 0;

      const newItem = {
        ...product,
        uniqueId,
        qty: quantity,
        basePrice: basePrice,
        price: basePrice,
        priceValue: basePrice,
        name: product.name || product.title || 'Product',
        title: product.title || product.name || 'Product',
        id: product.id || product.productId || Date.now(),
        productId: product.productId || product.id,
        addedAt: Date.now(),
        updatedAt: Date.now(),
      };
      return [...prevItems, newItem];
    });
  }, []);

  // QUANTITY / REMOVE / CLEAR
  const increaseQty = useCallback((uniqueId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.uniqueId === uniqueId
          ? { ...item, qty: (item.qty || 1) + 1, updatedAt: Date.now() }
          : item
      )
    );
  }, []);

  const decreaseQty = useCallback((uniqueId) => {
    setCartItems(prev => {
      const item = prev.find(i => i.uniqueId === uniqueId);
      if (!item) return prev;
      if ((item.qty || 1) <= 1) {
        return prev.filter(i => i.uniqueId !== uniqueId);
      }
      return prev.map(i =>
        i.uniqueId === uniqueId
          ? { ...i, qty: (i.qty || 1) - 1, updatedAt: Date.now() }
          : i
      );
    });
  }, []);

  const removeFromCart = useCallback((uniqueId) => {
    setCartItems(prev => prev.filter(item => item.uniqueId !== uniqueId));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
  }, []);

  // UPDATE CART FROM SERVER API RESPONSE (full replacement)
  const updateCartFromAPI = useCallback((apiCartItems) => {
    if (!apiCartItems || apiCartItems.length === 0) {
      setCartItems([]);
      return;
    }

    const transformed = apiCartItems.map(item => {
      let selectedSize, selectedFragrance;
      let selectedSizeId, selectedFragranceId;

      if (item.selectedAttributeDetails && Array.isArray(item.selectedAttributeDetails)) {
        item.selectedAttributeDetails.forEach((attr, index) => {
          if (attr.attribute?.name?.toLowerCase().includes('size') || index === 0) {
            selectedSize = attr.value || '';
            selectedSizeId = attr.id || attr.optionId || '';
          } else {
            selectedFragrance = attr.value || '';
            selectedFragranceId = attr.id || attr.optionId || '';
          }
        });
      } else if (item.selectedAttributes) {
        try {
          const attrs = JSON.parse(item.selectedAttributes);
          if (Array.isArray(attrs)) {
            if (attrs.length >= 1) selectedSize = attrs[0]?.toString() || '';
            if (attrs.length >= 2) selectedFragrance = attrs[1]?.toString() || '';
          }
        } catch (e) {}
      }

      let image;
      if (item.selectedAttributeDetails?.[0]?.images?.[0]) {
        image = item.selectedAttributeDetails[0].images[0];
      } else if (item.product?.images?.[0]) {
        image = item.product.images[0];
      } else if (item.image) {
        image = item.image;
      }

      const basePrice = cleanPrice(item.finalPrice) || cleanPrice(item.price) || cleanPrice(item.product?.price) || 0;

      return {
        uniqueId: item.id ? `api-${item.id}` : `api-${Date.now()}-${Math.random()}`,
        id: item.productId || item.product?.id || item.id,
        cartItemId: item.id,
        productId: item.productId || item.product?.id,
        name: item.product?.name || item.name || 'Product',
        title: item.product?.name || item.name || 'Product',
        basePrice: basePrice,
        price: basePrice,
        priceValue: basePrice,
        qty: item.quantity || item.qty || 1,
        ...(selectedSizeId && { selectedSizeId }),
        ...(selectedFragranceId && { selectedFragranceId }),
        ...(selectedSize && { selectedSize }),
        ...(selectedFragrance && { selectedFragrance }),
        ...(image && { image, img: image, img1: image }),
        finalPrice: item.finalPrice,
        product: item.product,
        fromAPI: true,
      };
    });

    setCartItems(transformed);
  }, []);

  const mergeCarts = useCallback((apiCartItems) => {
    if (!apiCartItems || apiCartItems.length === 0) return;

    const apiItems = apiCartItems.map(item => {
      let selectedSize, selectedFragrance, selectedSizeId, selectedFragranceId, image;
      if (item.selectedAttributeDetails) {
        item.selectedAttributeDetails.forEach((attr, index) => {
          if (attr.attribute?.name?.toLowerCase().includes('size') || index === 0) {
            selectedSize = attr.value || '';
            selectedSizeId = attr.id || attr.optionId || '';
          } else {
            selectedFragrance = attr.value || '';
            selectedFragranceId = attr.id || attr.optionId || '';
          }
        });
      } else {
        try {
          const attrs = JSON.parse(item.selectedAttributes);
          if (Array.isArray(attrs)) {
            selectedSize = attrs[0] || '';
            selectedFragrance = attrs[1] || '';
          }
        } catch (e) {}
      }
      if (item.selectedAttributeDetails?.[0]?.images?.[0]) {
        image = item.selectedAttributeDetails[0].images[0];
      }

      const basePrice = cleanPrice(item.finalPrice) || cleanPrice(item.price) || 0;

      return {
        uniqueId: `api-${item.id || Date.now()}`,
        cartItemId: item.id,
        productId: item.productId,
        qty: item.quantity || 1,
        ...(selectedSizeId && { selectedSizeId }),
        ...(selectedFragranceId && { selectedFragranceId }),
        ...(selectedSize && { selectedSize }),
        ...(selectedFragrance && { selectedFragrance }),
        name: item.product?.name || 'Product',
        title: item.product?.name || 'Product',
        basePrice: basePrice,
        price: basePrice,
        priceValue: basePrice,
        ...(image && { image, img: image }),
        id: item.productId,
        fromAPI: true,
      };
    });

    const apiMap = new Map();
    apiItems.forEach(item => {
      const key = `${item.productId}-${item.selectedSizeId || item.selectedSize || ''}-${item.selectedFragranceId || item.selectedFragrance || ''}`;
      apiMap.set(key, item);
    });

    const merged = [...apiItems];

    cartItems.forEach(localItem => {
      const key = `${localItem.id || localItem.productId}-${localItem.selectedSizeId || localItem.selectedSize || ''}-${localItem.selectedFragranceId || localItem.selectedFragrance || ''}`;
      if (!apiMap.has(key)) {
        merged.push({
          ...localItem,
          uniqueId: `local-${Date.now()}-${Math.random()}`,
          needsSync: true,
        });
      }
    });

    setCartItems(merged);
  }, [cartItems]);

  const syncLocalCartToServer = useCallback(async () => {
    const token = localStorage.getItem('token');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (!token || !isLoggedIn || cartItems.length === 0) return;

    setIsSyncing(true);
    try {
      const updatedCart = await syncCartAPI(cartItems);
      const serverCart = await getCartAPI();
      mergeCarts(serverCart.data?.items || serverCart.items || serverCart);
      localStorage.removeItem('cartItems');
    } catch (error) {
      console.error('Failed to sync cart with server', error);
    } finally {
      setIsSyncing(false);
    }
  }, [cartItems, mergeCarts]);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const isInCart = useCallback((uniqueId) => cartItems.some(i => i.uniqueId === uniqueId), [cartItems]);
  const getItemQty = useCallback((uniqueId) => {
    const item = cartItems.find(i => i.uniqueId === uniqueId);
    return item ? (item.qty || 1) : 0;
  }, [cartItems]);

  const value = {
    cartItems,
    cartCount,
    subtotal,
    isCartOpen,
    isSyncing,
    openCart,
    closeCart,
    addToCart,
    increaseQty,
    decreaseQty,
    removeFromCart,
    clearCart,
    isInCart,
    getItemQty,
    updateCartFromAPI,
    mergeCarts,
    syncLocalCartToServer,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};